import Head from 'next/head';
import type { Frame, Link } from '~/lib/data-contract';
import { useFrame } from '~/shared/utils';
import { getRuntimeConfig } from '~/shared/utils/config';

/**
 * JSON-LD for the front page (P20FrontPage).
 *
 * Emits a single @graph containing WebSite, Organization (OnlineStore),
 * ShippingService, MerchantReturnPolicy and MemberProgram, wired together with
 * @id references so each entity is described exactly once.
 *
 * Everything that exists in the frame payload is read from it, so the markup
 * follows content changes without a code deploy. Only the values the CMS does
 * not expose at all are constants, and each one is marked below.
 */

/**
 * Fallbacks for the company details normally parsed out of `footer.contactText`
 * ("Imerco A/S, Smedeholm 16, 2730 Herlev, CVR-nr. 26 57 25 17"). They are only
 * used if an editor reformats that line beyond recognition.
 */
const COMPANY_FALLBACK = {
    name: 'Imerco',
    legalName: 'Imerco A/S',
    vatID: 'DK26572517',
    telephone: '+45 98 18 98 38',
    email: 'kundeservice@imerco.dk',
    streetAddress: 'Smedeholm 16',
    postalCode: '2730',
    addressLocality: 'Herlev',
} as const;

/**
 * Publicly reachable logo URL, as supplied in the specification.
 *
 * The in-app logo is inlined as an SVG React component (~/icons/logo.svg), which
 * has no URL of its own. `footer.logoImage` is a CMS-hosted alternative, but it
 * is the white-on-transparent variant and therefore unusable on the white
 * background Google renders logos against.
 */
const LOGO_URL =
    'https://integration.imerco.dk/cdn-cgi/image/width=1920,format=auto,quality=65/https://integration.imerco.dk/api/media/scbbbzgn/hero-800-1066px-76.jpg?ud=wNpFsNLc3gg';

/**
 * TODO(IMC-XXXX): freight rates have no CMS field and the two places that state
 * them disagree — `market.freightPolicyText` says "Fra 29,00" while
 * `market.checkoutFaq` says "Fragtpriser fra 19 kr.". These constants follow
 * freightPolicyText; confirm against the specification before release.
 */
const COLLECTION_POINT_RATE = 29;
const HOME_DELIVERY_RATE = 29;

/**
 * Free delivery to a private address. `market.minimumBasketTotalForFreeShipping`
 * only covers the parcel-shop threshold, so this one mirrors
 * `market.freightPolicyText` ("Gratis fragt til privatadresse ved køb over
 * 1000,00").
 */
const FREE_HOME_DELIVERY_THRESHOLD = 1000;

/** Used only if the return window cannot be read out of the USP content. */
const RETURN_DAYS_FALLBACK = 90;

const DEFAULT_SITE_URL = 'https://www.imerco.dk';
const DEFAULT_CURRENCY = 'DKK';
const DEFAULT_CULTURE = 'da-dk';
const FREE_COLLECTION_POINT_THRESHOLD_FALLBACK = 500;

const DEFAULT_SHIPPING_POLICY_PATH = '/ansvarlighed/forretningsbetingelser';
const DEFAULT_RETURN_POLICY_PATH = '/ansvarlighed/forretningsbetingelser';
const DEFAULT_MEMBER_PROGRAM_PATH = '/imercoplus';
const DEFAULT_SEARCH_PATH = '/search';

const stripHtml = (value?: string) =>
    (value || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

/** "da-dk" -> { language: 'da', country: 'DK', locale: 'da-DK' } */
const parseCulture = (culture?: string) => {
    const [language = 'da', region = 'dk'] = (culture || DEFAULT_CULTURE).split('-');

    return {
        language: language.toLowerCase(),
        country: region.toUpperCase(),
        locale: `${language.toLowerCase()}-${region.toUpperCase()}`,
    };
};

/** "Imerco A/S, Smedeholm 16, 2730 Herlev, CVR-nr. 26 57 25 17" */
const parseCompany = (contactText?: string) => {
    const match = stripHtml(contactText).match(
        /^(.+?),\s*(.+?),\s*(\d{4})\s+(.+?),\s*CVR(?:-nr)?\.?\s*([\d\s]{8,})/i,
    );

    if (!match) {
        return COMPANY_FALLBACK;
    }

    const [, legalName, streetAddress, postalCode, addressLocality, cvr] = match;

    return {
        ...COMPANY_FALLBACK,
        legalName: legalName.trim(),
        streetAddress: streetAddress.trim(),
        postalCode,
        addressLocality: addressLocality.trim(),
        vatID: `DK${cvr.replace(/\D/g, '')}`,
    };
};

/** "tel:98189838" -> "+45 98 18 98 38" */
const parseTelephone = (link?: Link, countryCode = '+45') => {
    const digits = (link?.url || '').startsWith('tel:')
        ? (link?.url || '').slice(4).replace(/\D/g, '')
        : '';

    if (digits.length !== 8) {
        return undefined;
    }

    return `${countryCode} ${digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim()}`;
};

const parseEmail = (html?: string) => html?.match(/mailto:([^"'?\s>]+)/i)?.[1];

/** "Byttefrist — 90 dages returret i alle butikker" -> 90 */
const parseReturnDays = (frame: Frame) => {
    const usps = [
        ...(frame.market?.checkoutUsps || []),
        ...(frame.market?.uniqueSellingPoints || []),
    ];

    for (const usp of usps) {
        const text = stripHtml(`${usp.headline} ${usp.text}`);

        if (!/byttefrist|returret/i.test(text)) {
            continue;
        }

        const days = text.match(/(\d+)\s*dage/i);

        if (days) {
            return Number(days[1]);
        }
    }

    return undefined;
};

/** First matching footer link, tried label by label within a footer section. */
const findFooterUrl = (frame: Frame, section: string, labels: string[]) => {
    const children = frame.footer?.menu?.find((item) => item.header === section)?.children;

    for (const label of labels) {
        const url = children?.find((child) => child.link?.text === label)?.link?.url;

        if (url) {
            return url;
        }
    }

    return undefined;
};

const toAbsoluteUrl = (url: string | undefined, siteUrl: string) => {
    if (!url) {
        return undefined;
    }

    try {
        return new URL(url, `${siteUrl}/`).toString();
    } catch {
        return undefined;
    }
};

const toPositiveNumber = (value: unknown, fallback: number) => {
    const parsed = Number(value);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

/**
 * Two order-value bands: a flat rate below the free-shipping threshold and free
 * shipping at or above it. The upper bound stops one øre short of the threshold so
 * the bands never overlap.
 */
const shippingConditions = (rate: number, freeFrom: number, currency: string, country: string) => {
    const definedRegion = { '@type': 'DefinedRegion', addressCountry: country };

    return [
        {
            '@type': 'ShippingConditions',
            shippingOrigin: definedRegion,
            shippingDestination: definedRegion,
            orderValue: {
                '@type': 'MonetaryAmount',
                minValue: 0,
                maxValue: Number((freeFrom - 0.01).toFixed(2)),
                currency,
            },
            shippingRate: { '@type': 'MonetaryAmount', value: rate, currency },
        },
        {
            '@type': 'ShippingConditions',
            shippingOrigin: definedRegion,
            shippingDestination: definedRegion,
            orderValue: { '@type': 'MonetaryAmount', minValue: freeFrom, currency },
            shippingRate: { '@type': 'MonetaryAmount', value: 0, currency },
        },
    ];
};

export const HomePageSchema = () => {
    const { data: frame } = useFrame();
    const { config } = getRuntimeConfig();

    if (!frame) {
        return null;
    }

    const siteUrl = (config.NEXT_PUBLIC_URL || DEFAULT_SITE_URL).replace(/\/$/, '');
    const { language, country, locale } = parseCulture(frame.culture);
    const company = parseCompany(frame.footer?.contactText);
    const telephone =
        parseTelephone(frame.market?.receipt?.SupportPhoneButton) ?? COMPANY_FALLBACK.telephone;
    const email = parseEmail(frame.market?.receipt?.supportText) ?? COMPANY_FALLBACK.email;
    const currency = frame.market?.currency?.currency || DEFAULT_CURRENCY;
    const returnDays = parseReturnDays(frame) ?? RETURN_DAYS_FALLBACK;

    const freeCollectionPointFrom = toPositiveNumber(
        frame.market?.minimumBasketTotalForFreeShipping,
        FREE_COLLECTION_POINT_THRESHOLD_FALLBACK,
    );

    const shippingPolicyUrl =
        toAbsoluteUrl(findFooterUrl(frame, 'Kundeservice', ['Fragt og levering']), siteUrl) ||
        toAbsoluteUrl(DEFAULT_SHIPPING_POLICY_PATH, siteUrl);

    const returnPolicyUrl =
        toAbsoluteUrl(findFooterUrl(frame, 'Kundeservice', ['Bytte og retur']), siteUrl) ||
        toAbsoluteUrl(frame.market?.orderRequirementsLink, siteUrl) ||
        toAbsoluteUrl(DEFAULT_RETURN_POLICY_PATH, siteUrl);

    const memberProgramUrl =
        toAbsoluteUrl(frame.market?.membershipTermsLink, siteUrl) ||
        toAbsoluteUrl(
            findFooterUrl(frame, 'ImercoPlus', ['Fordele og betingelser', 'Se alt om ImercoPlus']),
            siteUrl,
        ) ||
        toAbsoluteUrl(DEFAULT_MEMBER_PROGRAM_PATH, siteUrl);

    const searchPageUrl =
        toAbsoluteUrl(frame.settings?.pageReferences?.searchPage?.url, siteUrl) ||
        toAbsoluteUrl(DEFAULT_SEARCH_PATH, siteUrl);

    const sameAs =
        frame.footer?.additionalIcons?.flatMap((icon) => {
            const url = toAbsoluteUrl(icon.link?.url, siteUrl);

            return url && url.startsWith('http') ? [url] : [];
        }) || [];

    const websiteId = `${siteUrl}/#website`;
    const organizationId = `${siteUrl}/#organization`;
    const logoId = `${siteUrl}/#logo`;
    const collectionPointShippingId = `${siteUrl}/#shipping-collection-point`;
    const homeDeliveryShippingId = `${siteUrl}/#shipping-home-delivery`;
    const returnPolicyId = `${siteUrl}/#return-policy`;
    const memberProgramId = `${siteUrl}/#imerco-plus`;
    const memberProgramTierId = `${siteUrl}/#imerco-plus-tier`;

    const schema = {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'WebSite',
                '@id': websiteId,
                url: `${siteUrl}/`,
                name: COMPANY_FALLBACK.name,
                inLanguage: locale,
                publisher: { '@id': organizationId },
                potentialAction: {
                    '@type': 'SearchAction',
                    target: {
                        '@type': 'EntryPoint',
                        urlTemplate: `${searchPageUrl}?query={search_term_string}`,
                    },
                    'query-input': 'required name=search_term_string',
                },
            },
            {
                '@type': 'OnlineStore',
                '@id': organizationId,
                name: COMPANY_FALLBACK.name,
                legalName: company.legalName,
                url: `${siteUrl}/`,
                vatID: company.vatID,
                logo: {
                    '@type': 'ImageObject',
                    '@id': logoId,
                    url: LOGO_URL,
                    contentUrl: LOGO_URL,
                },
                address: {
                    '@type': 'PostalAddress',
                    streetAddress: company.streetAddress,
                    postalCode: company.postalCode,
                    addressLocality: company.addressLocality,
                    addressCountry: country,
                },
                contactPoint: {
                    '@type': 'ContactPoint',
                    contactType: 'customer service',
                    telephone,
                    email,
                    availableLanguage: language,
                },
                sameAs,
                hasShippingService: [
                    { '@id': collectionPointShippingId },
                    { '@id': homeDeliveryShippingId },
                ],
                hasMerchantReturnPolicy: { '@id': returnPolicyId },
                hasMemberProgram: { '@id': memberProgramId },
            },
            {
                '@type': 'ShippingService',
                '@id': collectionPointShippingId,
                name: 'Levering til pakkeshop, pakkeboks eller erhvervsadresse',
                url: shippingPolicyUrl,
                description: `Levering til pakkeshop, pakkeboks eller erhvervsadresse. Gratis fragt ved køb over ${freeCollectionPointFrom},00.`,
                fulfillmentType: 'https://schema.org/FulfillmentTypeCollectionPoint',
                shippingConditions: shippingConditions(
                    COLLECTION_POINT_RATE,
                    freeCollectionPointFrom,
                    currency,
                    country,
                ),
            },
            {
                '@type': 'ShippingService',
                '@id': homeDeliveryShippingId,
                name: 'Levering til privatadresse',
                url: shippingPolicyUrl,
                description: `Levering til privatadresse. Gratis fragt ved køb over ${FREE_HOME_DELIVERY_THRESHOLD},00.`,
                fulfillmentType: 'https://schema.org/FulfillmentTypeDelivery',
                shippingConditions: shippingConditions(
                    HOME_DELIVERY_RATE,
                    FREE_HOME_DELIVERY_THRESHOLD,
                    currency,
                    country,
                ),
            },
            {
                '@type': 'MerchantReturnPolicy',
                '@id': returnPolicyId,
                name: 'Imercos bytte- og returret',
                url: returnPolicyUrl,
                merchantReturnLink: returnPolicyUrl,
                applicableCountry: country,
                returnPolicyCountry: country,
                returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
                merchantReturnDays: returnDays,
                returnMethod: [
                    'https://schema.org/ReturnInStore',
                    'https://schema.org/ReturnByMail',
                ],
                refundType: 'https://schema.org/FullRefund',
                // `returnFees` is deliberately omitted: Google requires
                // `returnShippingFeesAmount` alongside `ReturnShippingFees`, and the
                // specification does not state the return-shipping cost yet.
            },
            {
                '@type': 'MemberProgram',
                '@id': memberProgramId,
                name: 'ImercoPlus',
                url: memberProgramUrl,
                description:
                    'ImercoPlus er Imercos gratis kundeklub med medlemspriser, bonus og medlemsfordele.',
                hostingOrganization: { '@id': organizationId },
                hasTiers: [{ '@id': memberProgramTierId }],
            },
            {
                '@type': 'MemberProgramTier',
                '@id': memberProgramTierId,
                name: 'ImercoPlus',
                url: memberProgramUrl,
                isTierOf: { '@id': memberProgramId },
                hasTierRequirement: 'Gratis medlemskab',
                hasTierBenefit: [
                    'https://schema.org/TierBenefitLoyaltyPrice',
                    'https://schema.org/TierBenefitLoyaltyPoints',
                ],
                // TODO(IMC-XXXX): add `membershipPointsEarned` once the specification
                // states the earn rate and its unit.
            },
        ],
    };

    return (
        <Head>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(schema).replace(/</g, '\\u003c'),
                }}
            />
        </Head>
    );
};
